import React, { useEffect, useMemo, useCallback } from 'react';
import { Typography, Box, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import chaletApi from 'api/chaletApi';
import { useAppSelector } from 'redux/hook';
import { Order } from 'types/Order';
import { OrderHistoryItem } from 'ui/components/partials/Order/OrderHistoryItem';

type ListItem = {
  type: 'date-header' | 'order';
  data: string | Order;
  id: string;
};

const ITEM_HEIGHT = 60; // Height for order summary row
const DATE_HEADER_HEIGHT = 50; // Height for date headers

export const OrdersHistory = () => {
  const { user } = useAppSelector((s) => s.user);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [allOrders, setAllOrders] = React.useState<Order[]>([]);
  const [hasNextPage, setHasNextPage] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const [getOrdersHistory, { data, isLoading, error }] =
    chaletApi.useLazyGetOrdersHistoryPaginatedQuery();

  // Group orders by date and create list items
  const listItems: ListItem[] = useMemo(() => {
    if (!allOrders || allOrders.length === 0) {
      return [];
    }

    const items: ListItem[] = [];
    let currentDate: string | null = null;

    allOrders.forEach((order) => {
      const orderDate = format(parseISO(order.createdAt), 'yyyy-MM-dd');

      // Add date header if this is a new date
      if (currentDate !== orderDate) {
        currentDate = orderDate;
        items.push({
          type: 'date-header',
          data: orderDate,
          id: `date-${orderDate}`
        });
      }

      // Add order item
      items.push({
        type: 'order',
        data: order,
        id: `order-${order.id}`
      });
    });

    return items;
  }, [allOrders]);

  // Load initial data
  const loadInitialData = useCallback(() => {
    if (user?.branchWork?.branch?.id) {
      setCurrentPage(1);
      setAllOrders([]);
      setHasNextPage(true);
      getOrdersHistory({
        branchId: user.branchWork.branch.id,
        page: 1,
        limit: 20
      });
    }
  }, [user, getOrdersHistory]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Handle API response
  useEffect(() => {
    if (data && data.currentPage === currentPage) {
      if (data.currentPage === 1) {
        setAllOrders(data.orders);
      } else {
        setAllOrders((prev) => [...prev, ...data.orders]);
      }
      setHasNextPage(data.currentPage < data.totalPages);
      setIsLoadingMore(false);
    }
  }, [data, currentPage]);

  // Load more data
  const loadMoreItems = useCallback(async () => {
    if (isLoadingMore || !hasNextPage || !user?.branchWork?.branch?.id) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    getOrdersHistory({
      branchId: user.branchWork.branch.id,
      page: nextPage,
      limit: 20
    });
  }, [currentPage, hasNextPage, isLoadingMore, user, getOrdersHistory]);

  // Check if item is loaded for infinite loader
  const isItemLoaded = useCallback(
    (index: number) => {
      return index < listItems.length;
    },
    [listItems.length]
  );

  // Get item size for virtualized list
  const getItemSize = useCallback(
    (index: number) => {
      const item = listItems[index];
      return item?.type === 'date-header' ? DATE_HEADER_HEIGHT : ITEM_HEIGHT;
    },
    [listItems]
  );

  // Render list item
  const renderItem = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const item = listItems[index];

      if (!item) {
        return (
          <div style={style}>
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          </div>
        );
      }

      if (item.type === 'date-header') {
        const dateString = item.data as string;
        const formattedDate = format(parseISO(dateString), 'EEEE, d MMMM yyyy', { locale: es });

        return (
          <div style={style}>
            <Paper
              elevation={0}
              sx={{
                mx: 2,
                p: 1.5,
                background: 'rgba(25, 118, 210, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                boxShadow: '0 8px 32px rgba(25, 118, 210, 0.1)'
              }}>
              <Typography
                variant="body1"
                sx={{
                  textTransform: 'capitalize',
                  fontWeight: 500,
                  textAlign: 'center',
                  color: 'primary.main'
                }}>
                {formattedDate}
              </Typography>
            </Paper>
          </div>
        );
      }

      const order = item.data as Order;
      return (
        <div style={style}>
          <Box px={2}>
            <OrderHistoryItem order={order} />
          </Box>
        </div>
      );
    },
    [listItems]
  );

  if (isLoading && allOrders.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={400}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Cargando historial de órdenes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          Error al cargar el historial de órdenes. Por favor, inténtalo de nuevo.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: 2
      }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          borderRadius: 4
        }}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.5px'
            }}>
            Historial de Órdenes
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Gestiona y revisa el historial completo de órdenes
          </Typography>
        </Box>

        {listItems.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
              No hay órdenes en el historial
            </Typography>
            <Button variant="contained" onClick={loadInitialData} disabled={isLoading}>
              Reintentar
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              height: 'calc(100vh - 280px)',
              width: '100%',
              px: 2,
              '& .react-window-list::-webkit-scrollbar': {
                width: '0px',
                background: 'transparent'
              },
              '& .react-window-list': {
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }
            }}>
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={hasNextPage ? listItems.length + 1 : listItems.length}
              loadMoreItems={loadMoreItems}>
              {({ onItemsRendered, ref }) => (
                <List
                  ref={ref}
                  height={window.innerHeight - 280}
                  width="100%"
                  itemCount={hasNextPage ? listItems.length + 1 : listItems.length}
                  itemSize={getItemSize}
                  onItemsRendered={onItemsRendered}
                  overscanCount={5}
                  estimatedItemSize={60}>
                  {renderItem}
                </List>
              )}
            </InfiniteLoader>
          </Box>
        )}

        {isLoadingMore && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Cargando más órdenes...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
